import React, { useCallback, useMemo } from 'react';
import {
    IconCheckMark,
    IconArrowsChevronBottom,
    useClassnames,
    Dropdown,
    DropdownOption,
    IconFlagsCN,
    IconFlagsRU,
    IconFlagsGB
} from '@wb-tech/ui-react';
import { Text } from '@wb-tech/ui-react/v2';
import { useTranslation } from 'react-i18next';

import { api } from '#adapter/api/campaigns';
import { ELanguage, supportedLanguages } from '#core/const/locales';
import { useCountryCode } from '#hook/use-country-code';

import style from './index.module.pcss';

type TLanguage = ELanguage.RU | ELanguage.EN | ELanguage.ZH;

export const LanguageSelect = () => {
    const cn = useClassnames(style);
    const { i18n } = useTranslation();
    const { isChinese } = useCountryCode();

    const [updateSupplierLanguage] = api.endpoints.v5SupplierLanguagePost.useMutation();

    const icons = useMemo(
        () => ({
            [ELanguage.RU]: <IconFlagsRU svg={{ className: cn('language-select__icon-ru') }} />,
            [ELanguage.EN]: <IconFlagsGB svg={{ className: cn('language-select__icon-gb') }} />,
            [ELanguage.ZH]: <IconFlagsCN svg={{ className: cn('language-select__icon-cn') }} />
        }),
        [cn]
    );

    const getOption = useMemo(
        () => (lang?: string) => {
            return (
                <div className={cn('language-select__trigger-title')}>
                    <div className={cn('language-select__icon-circle')}>{icons[lang as TLanguage]}</div>

                    <Text className={cn('language-select__trigger-text')}>{lang?.toUpperCase()}</Text>
                </div>
            );
        },
        [cn, icons]
    );

    const selectedOption = useMemo(() => getOption(i18n.resolvedLanguage), [getOption, i18n.resolvedLanguage]);

    const onChangeLanguage = useCallback(
        (language: string) => {
            void i18n.changeLanguage(language).then(async () => {
                await updateSupplierLanguage({ body: { language } });
            }).catch((error) => { console.error(error); });
        },
        [i18n, updateSupplierLanguage]
    );

    return (
        <Dropdown
            className={cn('language-select')}
            triggerElement={(isOpen: boolean) => (
                <div className={cn('language-select__trigger')}>
                    {selectedOption}

                    <IconArrowsChevronBottom
                        svg={{
                            className: cn('language-select__trigger-icon', {
                                'language-select__trigger-icon_open': isOpen
                            })
                        }}
                    />
                </div>
            )}
            render={(isOpen, onClose) => {
                return supportedLanguages
                    .filter((item: string) => isChinese || item !== ELanguage.ZH)
                    .map((item: string) => {
                        const onClick = () => {
                            onChangeLanguage(item);
                            onClose();
                        };

                        return (
                            <DropdownOption
                                key={item}
                                onClick={onClick}
                                className={cn('language-select__trigger-option')}
                            >
                                {item === i18n.resolvedLanguage && (
                                    <IconCheckMark
                                        svg={{
                                            className: cn('language-select__option-checkmark')
                                        }}
                                    />
                                )}

                                {getOption(item)}
                            </DropdownOption>
                        );
                    });
            }}
        />
    );
};
